import {parseDocument, stringify} from "yaml";

const STEP = '  ';

function skeletonLines(indent) {
    return [
        `${indent}- description: `,
        `${indent}${STEP}facts: |`,
        `${indent}${STEP}${STEP}`,
        `${indent}${STEP}expected:`,
        `${indent}${STEP}${STEP}- `
    ];
}

const isBlank = (line) => line.trim() === '';
const isComment = (line) => line.trimStart().startsWith('#');
const indentOf = (line) => line.slice(0, line.length - line.trimStart().length);

function withSkeletonAfter(lines, lastLineIndex, indent) {
    const skeleton = skeletonLines(indent);
    const before = lines.slice(0, lastLineIndex + 1);
    const after = lines.slice(lastLineIndex + 1);
    const insertedAt = before.join('\n').length + 1;
    const result = [...before, '', ...skeleton, ...after];

    // The cursor goes at the end of the description line, ready to be filled in
    return {
        text: result.join('\n').replace(/\n*$/, '\n'),
        caret: insertedAt + 1 + skeleton[0].length
    };
}

function lastContentLine(lines, from, to) {
    for (let i = to; i >= from; i--) {
        if (!isBlank(lines[i])) {
            return i;
        }
    }
    return from;
}

/**
 * Adds the skeleton of a new test (description, facts, expected) at the end of the cases written in the editor,
 * leaving what's already there (comments, indentation, formatting) as it is.
 *
 * @return {?{text: string, caret: number}} the new text and where to put the cursor, null if the YAML has
 * errors and the list of cases can't be found
 */
export function appendNewTest(text) {
    if (text.trim() === '') {
        const skeleton = skeletonLines(STEP);
        return {
            text: `cases:\n${skeleton.join('\n')}\n`,
            caret: 'cases:\n'.length + skeleton[0].length
        };
    }

    const lines = text.split('\n');

    // "cases:" followed by its list: the new test goes at the end of the list
    const casesLine = lines.findIndex(line => /^cases\s*:\s*$/.test(line));
    if (casesLine !== -1) {
        let end = lines.length - 1;
        for (let i = casesLine + 1; i < lines.length; i++) {
            if (!isBlank(lines[i]) && !isComment(lines[i]) && indentOf(lines[i]) === '' && !lines[i].startsWith('-')) {
                end = i - 1;
                break;
            }
        }
        const firstItem = lines.findIndex((line, i) => i > casesLine && i <= end && /^\s*-\s/.test(line));
        const indent = firstItem === -1 ? STEP : indentOf(lines[firstItem]);
        return withSkeletonAfter(lines, lastContentLine(lines, casesLine, end), indent);
    }

    // A bare list of cases
    const firstContent = lines.findIndex(line => !isBlank(line) && !isComment(line));
    if (firstContent !== -1 && /^\s*-\s/.test(lines[firstContent])) {
        return withSkeletonAfter(lines, lastContentLine(lines, firstContent, lines.length - 1), indentOf(lines[firstContent]));
    }

    // Anything else that is still valid (for instance JSON pasted from Swagger): write it again as YAML
    const document = parseDocument(text);
    if (document.errors.length > 0) {
        return null;
    }
    const data = document.toJS();
    const cases = Array.isArray(data) ? data : data?.cases;
    if (!Array.isArray(cases)) {
        return null;
    }
    const rewritten = stringify({cases: [...cases, {description: '', facts: '', expected: ['']}]}, {lineWidth: 0});
    return {text: rewritten, caret: rewritten.length};
}
